// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import setCanSendReturnRequest from "@insite/client-framework/Store/Pages/RequestRma/Handlers/SetCanSendReturnRequest";
import setOrderLines from "@insite/client-framework/Store/Pages/RequestRma/Handlers/SetOrderLines";
import translate from "@insite/client-framework/Translate";
import { OrderLineModel, OrderModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LineContainer, { LineHeader, lineStyle } from "@monsma/App/Components/LineContainer/LineContainer";
import xS3UploadFileRequest from "@monsma/App/Handlers/RequestRma/S3UploadFileRequest";
import { xAppState } from "@monsma/App/Store/Reducers";
import { isStillDisabledDueEligibilityLimit } from "@monsma/App/Utilities/helpers";
import { cartLineCardExpandedStyles } from "@monsma/Overrides/Components/CartLineCardExpanded";
import Notify from "@monsma/XDL/Components/Notify";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { AnyStyledComponent, css } from "styled-components";

const mapStateToProps = (state: xAppState) => {
    const { customSettings } = getSettingsCollection(state);
    return {
        orderLines: state.pages.requestRma.orderLines,
        daysOfRmaEligibility: (customSettings as any).daysOfRmaEligibility,
        uploadedFilesObject: state.xS3UploadFile.uploadedFilesObject,
    };
};

const mapDispatchToProps = {
    setOrderLines,
    setCanSendReturnRequest,
    xS3UploadFileRequest,
    S3UploadFileRequest:
        ({ result, lineNumber, isLoading }: { result: string; lineNumber: number; isLoading: boolean }) =>
        (dispatch: Dispatch<any>) => {
            dispatch({
                uploadedFiles: result,
                lineNumber,
                isLoading,
                type: "xRequestRma/S3UploadFileRequest",
            });
        },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

// xng-custom XStyle injection, controlled from extentions
export interface XStyles {
    titleText?: TypographyProps;
    qtyToReturnWrapper?: InjectableCss;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    subTotalWrapper?: InjectableCss;
    requestRmaMsg?: InjectableCss;
    uploadImageWrapper: InjectableCss;
    fileUploader?: FileUploadPresentationProps;
    uploadingWrapper?: InjectableCss;
    orderWithinRmaEligibilityLimitMsg?: InjectableCss;
}

export interface RequestRmaProductListStyles {
    gridContainer?: GridContainerProps;
    orderLineCardContainer?: GridContainerProps;
    orderLineCardGridContainer?: GridContainerProps;
    orderLineCardImageGridItem?: GridItemProps;
    orderLineCardImage?: LazyImageProps;
    orderLineCardInfoGridItem?: GridItemProps;
    orderLineCardInfoGridContainer?: GridContainerProps;
    returnReasonGridItem?: GridItemProps;
    qtyToReturnTextField?: TextFieldPresentationProps;
    returnReasonSelect?: SelectPresentationProps;
    productInfoGridItem?: GridItemProps;
    productInfoGridContainer?: GridContainerProps;
    productInfoBrandDescriptionGridItem?: GridItemProps;
    productInfoBrandDescriptionGridContainer?: GridContainerProps;
    productInfoBrandGridItem?: GridItemProps;
    productInfoDescriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productBrandStyles?: ProductBrandStyles;
    productInfoPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoGridContainer?: GridContainerProps;
    orderLinePriceGridItem?: GridItemProps;
    productInfoQtyOrderedGridItem?: GridItemProps;
    productInfoSubtotalGridItem?: GridItemProps;

    // custom
    xRequestRmaProductListStyles?: XStyles;
}

export const requestRmaProductListStyles: RequestRmaProductListStyles = {
    gridContainer: {
        css: css`
            margin-top: 35px;
            margin-bottom: 25px;
        `,
    },
    orderLineCardImageGridItem: {
        width: [3, 3, 2, 2, 1],
        printWidth: 1,
        css: css`
            padding-left: 0;
        `,
    },
    orderLineCardInfoGridItem: { width: [9, 9, 7, 7, 8] },
    orderLineCardInfoGridContainer: { gap: 10 },
    returnReasonGridItem: {
        width: [9, 9, 3, 3, 3],
        printWidth: 0,
        css: css`
            flex-direction: column;
            margin-left: auto !important;
        `,
    },
    qtyToReturnTextField: {
        cssOverrides: {
            formInputWrapper: css`
                width: 90px;
            `,
        },
    },
    productInfoGridItem: { width: [12, 12, 8, 8, 8] },
    productInfoGridContainer: { gap: 5 },
    productInfoBrandDescriptionGridItem: { width: 12 },
    productInfoBrandDescriptionGridContainer: { gap: 5 },
    productInfoBrandGridItem: { width: 12 },
    productInfoDescriptionGridItem: { width: 12 },
    productInfoPartNumbersGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbers: {
        erpNumberLabelText: {
            css: css`
                margin-right: 8px;
            `,
        },
        customerNameLabelText: {
            css: css`
                margin-right: 8px;
            `,
        },
        manufacturerItemLabelText: {
            css: css`
                margin-right: 8px;
            `,
        },
    },
    orderLineInfoGridItem: {
        width: [12, 12, 4, 4, 4],
        printWidth: 6,
    },
    orderLineInfoGridContainer: { gap: 10 },
    orderLinePriceGridItem: {
        width: [12, 12, 12, 12, 7],
        printWidth: 7,
    },
    productInfoQtyOrderedGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
    },
    productInfoSubtotalGridItem: { width: 12 },
};

const ProductInfo = ({ orderLine }: { orderLine: OrderLineModel }) => {
    const [styles] = React.useState(() =>
        mergeToNew(
            mergeToNew(mergeToNew(cartLineCardExpandedStyles, requestRmaProductListStyles), lineStyle),
            cartLineCardExpandedStyles.xCartLine,
        ),
    );

    return (
        <>
            <ProductPartNumbers
                productNumber={orderLine.productErpNumber}
                customerProductNumber={orderLine.customerProductNumber}
                manufacturerItem={orderLine.manufacturerItem}
                extendedStyles={styles.productPartNumbers}
            />
            {orderLine.brand && <ProductBrand brand={orderLine.brand} extendedStyles={styles.productBrand} />}
            <ProductDescription product={orderLine} extendedStyles={styles.productDescription} />
        </>
    );
};

const maxFileSize = 5 * 1024 * 1024; // 2.5MB

const OrderLineCard = (props: {
    order: OrderModel;
    orderLine: OrderLineModel;
    totalQuantity: number;
    qtyChangeHandler: (event: React.ChangeEvent<HTMLInputElement>, lineNumber: number) => void;
    returnReasonChangeHandler: (event: React.ChangeEvent<HTMLSelectElement>, lineNumber: number) => void;
    uploadFileChangeHandler: (fileUploaded: File, lineNumber: number, totalfilesCount: number) => void;
    clearFileUploadedCount: () => void;
    lineNumberLoadingData: {
        isLoading: boolean;
        lineNumber: number;
    };
    uploadedFilesObject: {
        [key: string]: Array<string>;
    };
}) => {
    const {
        order,
        orderLine,
        totalQuantity,
        qtyChangeHandler,
        returnReasonChangeHandler,
        uploadFileChangeHandler,
        clearFileUploadedCount,
        lineNumberLoadingData,
        uploadedFilesObject,
    } = props;
    // xng-change: adding customizations  for file uploader functionality
    const minQtyToReturn = totalQuantity > 0 ? 0 : 1;
    const [isAboveMaxSize, setIsAboveMaxSize] = React.useState(false);
    const [fileName, setFileName] = React.useState("");

    const qtyToReturnError =
        totalQuantity > 0 &&
        ((orderLine.rmaQtyRequested > orderLine.qtyOrdered &&
            siteMessage("Field_Max_Number", translate("QTY Returning"), orderLine.qtyOrdered.toString())) ||
            (orderLine.rmaQtyRequested < minQtyToReturn &&
                siteMessage("Field_Min_Number", translate("QTY Returning"), minQtyToReturn.toString())));

    const updateUploadedFile = (event: React.ChangeEvent<AnyStyledComponent>, lineNumber: number) => {
        const { files } = event.target;
        const filesArray = Array.from(files);
        let limitFileSizeExceeded = false;
        filesArray.forEach((file: any) => {
            const size = file.size;
            if (size > maxFileSize) {
                limitFileSizeExceeded = true;
            }
        });

        setIsAboveMaxSize(limitFileSizeExceeded);

        if (!limitFileSizeExceeded) {
            clearFileUploadedCount();
            filesArray.forEach((file: any) => {
                uploadFileChangeHandler(file, lineNumber, filesArray.length);
            });
        }
    };

    const [styles] = React.useState(() =>
        mergeToNew(
            mergeToNew(mergeToNew(cartLineCardExpandedStyles, requestRmaProductListStyles), lineStyle),
            cartLineCardExpandedStyles.xCartLine,
        ),
    );

    // xng-change: restructuring using LineContainer
    return (
        <>
            <LineContainer data-test-selector={`cartline_expanded_${orderLine.productId}_${orderLine.unitOfMeasure}`}>
                <LineContainer.Image>
                    {orderLine.productUri && orderLine.isActiveProduct && (
                        <Link href={orderLine.productUri} {...styles.productImage?.linkWrappingImage}>
                            <LazyImage
                                {...styles.productImage?.image}
                                src={orderLine.mediumImagePath}
                                altText={
                                    orderLine.shortDescription ||
                                    ("description" in orderLine ? orderLine.description : "")
                                }
                            />
                        </Link>
                    )}
                </LineContainer.Image>
                <LineContainer.Info>
                    <ProductInfo orderLine={orderLine} />
                </LineContainer.Info>
                <LineContainer.Qty>
                    <SmallHeadingAndText
                        extendedStyles={styles.xRequestRmaProductListStyles?.headingAndTextStyles}
                        heading={translate("ordered")}
                        text={`${orderLine.qtyOrdered}`}
                    />
                </LineContainer.Qty>
                <LineContainer.Price>
                    <SmallHeadingAndText
                        extendedStyles={styles.xRequestRmaProductListStyles?.headingAndTextStyles}
                        heading={translate("Price")}
                        text={
                            orderLine.unitPriceDisplay +
                            (orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : "")
                        }
                    />
                    <StyledWrapper {...styles.xRequestRmaProductListStyles?.subTotalWrapper}>
                        <SmallHeadingAndText
                            extendedStyles={styles.xRequestRmaProductListStyles?.headingAndTextStyles}
                            heading={translate("Subtotal")}
                            text={orderLine.extendedUnitNetPriceDisplay}
                        />
                    </StyledWrapper>
                </LineContainer.Price>
                <LineContainer.Note>
                    <StyledWrapper {...styles.xRequestRmaProductListStyles?.uploadingWrapper}>
                        <StyledWrapper {...styles.xRequestRmaProductListStyles?.qtyToReturnWrapper}>
                            <TextField
                                {...styles.qtyToReturnTextField}
                                label={translate("QTY to Return")}
                                type="number"
                                value={orderLine.rmaQtyRequested.toString()}
                                min={minQtyToReturn}
                                max={orderLine.qtyOrdered}
                                error={qtyToReturnError}
                                data-test-selector={`requestRmaLine_qtyToReturn_${orderLine.id}`}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    qtyChangeHandler(event, orderLine.lineNumber)
                                }
                                // XNG-Changes: MON-1046
                                disabled={orderLine.properties.disableRMA?.toLowerCase() === "true"}
                            />
                            {!!order.returnReasons?.length && (
                                <Select
                                    label={translate("Return Reason")}
                                    {...styles.returnReasonSelect}
                                    value={orderLine.returnReason}
                                    required={orderLine.rmaQtyRequested > 0}
                                    error={
                                        orderLine.rmaQtyRequested > 0 &&
                                        !orderLine.returnReason &&
                                        siteMessage("Field_Required", translate("Return Reason"))
                                    }
                                    data-test-selector={`requestRmaLine_ReturnReason_${orderLine.id}`}
                                    onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                                        returnReasonChangeHandler(event, orderLine.lineNumber)
                                    }
                                    // XNG-Changes: MON-1046
                                    disabled={orderLine.properties.disableRMA?.toLowerCase() === "true"}
                                >
                                    <option value="">{translate("Select a Reason Code")}</option>
                                    {order.returnReasons?.map(returnReason => (
                                        <option key={returnReason} value={returnReason}>
                                            {returnReason}
                                        </option>
                                    ))}
                                </Select>
                            )}
                        </StyledWrapper>
                        <StyledWrapper
                            {...styles.xRequestRmaProductListStyles?.uploadImageWrapper}
                            className={orderLine.rmaQtyRequested <= 0 ? "uploadDisabled" : ""}
                        >
                            <StyledWrapper
                                css={css`
                                    position: relative;
                                    width: 100%;
                                `}
                            >
                                {lineNumberLoadingData.isLoading &&
                                    orderLine.lineNumber === lineNumberLoadingData.lineNumber && (
                                        <StyledWrapper
                                            css={css`
                                                display: flex;
                                                align-items: center;
                                                position: absolute;
                                                height: 100%;
                                                width: 100%;
                                                z-index: 1;
                                            `}
                                        >
                                            <LoadingSpinner
                                                css={css`
                                                    height: 20px;
                                                    width: 20px;
                                                    margin-right: 5px;
                                                    margin-bottom: 10px;
                                                    margin-left: auto;
                                                    margin-top: initial;
                                                `}
                                            />
                                        </StyledWrapper>
                                    )}
                                <FileUpload
                                    {...styles.xRequestRmaProductListStyles?.fileUploader}
                                    accept=".JPEG, .JPG, .TIFF, .GIF, .PNG, .svg, .pdf"
                                    label={translate("Upload Image / PDF")}
                                    multiple
                                    labelPosition="top"
                                    fileName={fileName}
                                    onFileChange={e => updateUploadedFile(e, orderLine.lineNumber)}
                                    uid={`fileUploader_${orderLine.lineNumber}`}
                                    disabled={lineNumberLoadingData.isLoading || orderLine.rmaQtyRequested <= 0}
                                    hideButton={true}
                                    error={
                                        (isAboveMaxSize ? translate("size must be less than 5MB") : undefined) ||
                                        (orderLine.rmaQtyRequested > 0 &&
                                            !orderLine.properties.hasUploadedFiles &&
                                            siteMessage("Field_Required", translate("Upload File")))
                                    }
                                    hint={translate("Max Size 5MB")}
                                    required={orderLine.rmaQtyRequested > 0}
                                    iconProps={{
                                        css: css`
                                            &&& {
                                                display: ${orderLine.lineNumber === lineNumberLoadingData.lineNumber &&
                                                lineNumberLoadingData.isLoading
                                                    ? "none"
                                                    : "flex"};
                                                top: 0;
                                                right: 0;
                                                width: 31px;
                                                height: 31px;
                                                padding: 2px;
                                            }
                                        `,
                                    }}
                                />
                            </StyledWrapper>

                            <StyledWrapper
                                css={css`
                                    margin-top: 4px;
                                `}
                            >
                                {uploadedFilesObject[orderLine.lineNumber] &&
                                    uploadedFilesObject[orderLine.lineNumber].map(uploadedFile => (
                                        <StyledWrapper
                                            key={uploadedFile}
                                            css={css`
                                                display: flex;
                                                align-items: center;
                                            `}
                                        >
                                            <Icon
                                                size={15}
                                                src="Folder"
                                                css={css`
                                                    margin-right: 5px;
                                                `}
                                            />
                                            <Typography
                                                css={css`
                                                    font-size: 13px;
                                                    overflow: hidden;
                                                    text-overflow: ellipsis;
                                                    white-space: nowrap;
                                                    display: block;
                                                `}
                                            >
                                                {uploadedFile}
                                            </Typography>
                                        </StyledWrapper>
                                    ))}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    {/* XNG-Changes: MON-1046 */}
                    {orderLine.properties.disableRMA?.toLowerCase() === "true" && (
                        <Notify
                            messageType="danger"
                            hideCloseButton
                            body={siteMessage("Custom_RequestRma_NonReturnableItemMsg")}
                        />
                    )}
                </LineContainer.Note>
            </LineContainer>
        </>
    );
};

const RequestRmaProductList: FC<Props> = ({
    orderLines,
    setOrderLines,
    setCanSendReturnRequest,
    daysOfRmaEligibility,
    xS3UploadFileRequest,
    uploadedFilesObject,
    S3UploadFileRequest,
}) => {
    const [totalQuantity, setTotalQuantity] = React.useState(0);
    const fileUploadedCount = React.useRef(0);
    const { value: order } = useContext(OrderStateContext);
    const [lineNumberLoadingData, setLineNumberLoadingData] = React.useState({
        isLoading: false,
        lineNumber: 0,
    });
    const toasterContext = useContext(ToasterContext);

    React.useEffect(() => {
        if (!order || !order.orderLines) {
            return;
        }

        setOrderLines({ orderLines: order.orderLines });
    }, [order?.orderLines]);

    const reasonsDefined = !!order?.returnReasons?.length;
    React.useEffect(() => {
        setCanSendReturnRequest({
            value:
                orderLines.some(line => line.rmaQtyRequested > 0 && (line.returnReason || !reasonsDefined)) &&
                !orderLines.some(
                    line =>
                        line.rmaQtyRequested > line.qtyOrdered ||
                        (line.rmaQtyRequested > 0 && !line.returnReason && reasonsDefined) ||
                        (line.rmaQtyRequested > 0 && !line.properties.hasUploadedFiles),
                ),
        });
    }, [orderLines]);

    if (!order || !order.orderLines || !order.orderLines.length) {
        return null;
    }

    const qtyChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, lineNumber: number) => {
        const qty = parseInt(event.target.value, 10);
        const updatedOrderLines = orderLines.map(line =>
            line.lineNumber === lineNumber ? { ...line, rmaQtyRequested: qty } : line,
        );
        setOrderLines({ orderLines: updatedOrderLines });

        let totalQty = 0;
        updatedOrderLines.forEach(orderLine => {
            totalQty += orderLine.rmaQtyRequested > 0 ? 1 : 0;
        });

        setTotalQuantity(totalQty);
    };

    const returnReasonChangeHandler = (event: React.FormEvent<HTMLSelectElement>, lineNumber: number) => {
        setOrderLines({
            orderLines: orderLines.map(line =>
                line.lineNumber === lineNumber ? { ...line, returnReason: event.currentTarget.value } : line,
            ),
        });
    };

    const styles = requestRmaProductListStyles;

    const clearFileUploadedCount = () => {
        fileUploadedCount.current = 0;
    };

    const uploadFileChangeHandler = (fileUploaded: File, lineNumber: number, totalfilesCount: number) => {
        if (fileUploaded) {
            const file = new FormData();
            file.append("file", fileUploaded);

            setLineNumberLoadingData({
                isLoading: true,
                lineNumber,
            });

            xS3UploadFileRequest({
                orderNumber: (order.webOrderNumber.length > 0 && order.webOrderNumber) || order.erpOrderNumber,
                lineNumber,
                file,
                onSuccess: result => {
                    if (fileUploadedCount.current + 1 === totalfilesCount) {
                        toasterContext.addToast({
                            body: translate("File(s) successfully uploaded"),
                            messageType: "success",
                        });

                        setOrderLines({
                            orderLines: orderLines.map(line =>
                                line.lineNumber === lineNumber
                                    ? { ...line, properties: { ...line.properties, hasUploadedFiles: true } }
                                    : line,
                            ) as any,
                        });

                        setLineNumberLoadingData({
                            isLoading: false,
                            lineNumber,
                        });
                        S3UploadFileRequest({ lineNumber, isLoading: false, result });
                    } else {
                        S3UploadFileRequest({ lineNumber, isLoading: true, result });
                    }
                    fileUploadedCount.current += 1;
                },
                onError: (errorMessage: string) => {
                    if (fileUploadedCount.current + 1 === totalfilesCount) {
                        toasterContext.addToast({
                            body: errorMessage,
                            messageType: "danger",
                        });
                        setLineNumberLoadingData({
                            isLoading: false,
                            lineNumber,
                        });
                    }
                    fileUploadedCount.current += 1;
                },
            });
        }
    };

    return (
        <>
            <Typography {...styles.xRequestRmaProductListStyles?.titleText}>{translate("Products")}</Typography>
            <LineHeader />
            {orderLines.map(orderLine => (
                <StyledWrapper key={orderLine.id}>
                    <OrderLineCard
                        order={order}
                        orderLine={orderLine}
                        totalQuantity={totalQuantity}
                        qtyChangeHandler={qtyChangeHandler}
                        returnReasonChangeHandler={returnReasonChangeHandler}
                        uploadFileChangeHandler={uploadFileChangeHandler}
                        clearFileUploadedCount={clearFileUploadedCount}
                        lineNumberLoadingData={lineNumberLoadingData}
                        uploadedFilesObject={uploadedFilesObject}
                    />
                </StyledWrapper>
            ))}
            <StyledWrapper {...styles.xRequestRmaProductListStyles?.requestRmaMsg}>
                {siteMessage("Custom_RequestRma_Message")}
            </StyledWrapper>
            <StyledWrapper {...styles.xRequestRmaProductListStyles?.orderWithinRmaEligibilityLimitMsg}>
                {isStillDisabledDueEligibilityLimit(order, daysOfRmaEligibility) && (
                    <Notify
                        messageType="info"
                        hideCloseButton
                        body={siteMessage("Custom_RequestRma_OrderWithinRmaEligibilityLimit")}
                    />
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RequestRmaProductList),
    definition: {
        allowedContexts: ["RequestRmaPage"],
        group: "Return Request (RMA)",
        fieldDefinitions: [],
    },
};

export default widgetModule;
